"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { UserState } from "@/types/user";
import { fetchUserInfo } from "@/lib/api/user";
import { hasAuthCookies, markLogoutPending } from "@/lib/cookies";
import { useGroupStore } from "@/lib/store/groupStore";
import Cookie from "js-cookie";
import { useRouter } from "@/i18n/navigation";

type UserContextValue = {
  user: UserState | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const loadUser = useCallback(async () => {
    if (!hasAuthCookies()) {
      setUser(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const userData = await fetchUserInfo();
      setUser(userData);

      // 获取 group 详情 - 参考 userLoginInfo.ts 的逻辑
      const groupStore = useGroupStore.getState();
      const orgId = Cookie.get("sg") || "-1";

      // 只在 orgId 不为 '-1' 时才获取 group 信息
      if (orgId !== "-1") {
        await groupStore.fetchGroupInfo(orgId);
      }
    } catch (e) {
      setUser(null);
      setError(e instanceof Error ? e.message : "NO_INFO_WHITE_LIST");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(() => {
    if (typeof window === "undefined") return;
    const redirect =
      window.location.pathname + window.location.search + window.location.hash;
    router.push(`/login?redirect=${encodeURIComponent(redirect)}`);
  }, [router]);

  const logout = useCallback(async () => {
    // 参考 fetch-api.ts 中 1002 拦截逻辑：跳转登录页并带 redirect 参数
    markLogoutPending();
    const p = window.location.pathname;
    const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
    const m = p.match(/\/(zh|en)(\/|$)/);
    const redirect = encodeURIComponent(
      p + window.location.search + window.location.hash,
    );
    const loginUrl = m
      ? p.slice(0, m.index) + "/" + m[1] + "/login?redirect=" + redirect
      : `${basePath}/zh/login`;
    window.location.replace(loginUrl);
  }, []);

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const value: UserContextValue = {
    user,
    loading,
    error,
    login,
    logout,
    refreshUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within UserProvider");
  }
  return ctx;
}
